import { ApiRequestError } from '../api/client'

export type WorkspaceProjectErrorKind = 'backendUnavailable' | 'notFound' | 'notFoundUnknown'

/**
 * Discriminated error state; the caller resolves each `kind` to localized copy.
 *
 * `notFound` REQUIRES a `projectId` because its localized message
 * (`workspace.projectError.notFoundMessage`) interpolates `{{projectId}}` — the
 * type makes it impossible to render "Could not load project undefined". When no
 * id is available, the state degrades to `notFoundUnknown`, which maps to a
 * separate, non-interpolated message (`notFoundMessageUnknown`).
 */
export type WorkspaceProjectErrorState =
  | { kind: 'backendUnavailable' }
  | { kind: 'notFound'; projectId: string }
  | { kind: 'notFoundUnknown' }

function isNetworkFailure(error: Error | null | undefined): boolean {
  return error instanceof TypeError
}

export function isBackendUnavailableProjectError(error: Error | null | undefined): boolean {
  if (error instanceof ApiRequestError) {
    return error.status >= 500
  }
  return isNetworkFailure(error)
}

export function getWorkspaceProjectErrorState(
  error: Error | null | undefined,
  projectId: string | undefined,
): WorkspaceProjectErrorState {
  if (isBackendUnavailableProjectError(error)) {
    return { kind: 'backendUnavailable' }
  }

  // A trimmed-empty id can't be interpolated into the not-found message, so fall
  // back to the id-less variant rather than rendering "project (unknown)".
  const trimmedId = projectId?.trim()
  if (!trimmedId) {
    return { kind: 'notFoundUnknown' }
  }

  return { kind: 'notFound', projectId: trimmedId }
}
